import type { OrgMember, OrgMemberRole, TeamService } from '@shopgnd/shared';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getAuthedDataClient, getCatalogClient } from '@/lib/amplify-client';

export type TeamServiceDraft = {
  name: string;
  description?: string;
  durationMin: number;
  priceCents: number;
  category?: string;
  memberId?: string | null;
  isActive?: boolean;
};

function mapService(row: Record<string, unknown>): TeamService {
  return {
    id: String(row.id),
    orgId: String(row.orgId),
    ownerUserId: String(row.ownerUserId),
    memberId: row.memberId ? String(row.memberId) : null,
    name: String(row.name),
    description: row.description ? String(row.description) : undefined,
    durationMin: Number(row.durationMin),
    priceCents: Number(row.priceCents),
    category: row.category ? String(row.category) : undefined,
    isActive: row.isActive !== false,
    legacyOfferingId: row.legacyOfferingId ? String(row.legacyOfferingId) : undefined,
  };
}

async function getTeamServiceUrl(): Promise<string | null> {
  const fromEnv = import.meta.env.VITE_TEAM_SERVICE_URL as string | undefined;
  if (fromEnv) return fromEnv;
  try {
    const res = await fetch('/amplify_outputs.json');
    if (res.ok) {
      const outputs = (await res.json()) as { custom?: { teamServiceUrl?: string } };
      return outputs.custom?.teamServiceUrl ?? null;
    }
  } catch {
    /* missing outputs */
  }
  return null;
}

async function requestHeaders(requireAuth: boolean): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (!requireAuth) return headers;
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('Sign in required.');
  headers.Authorization = `Bearer ${token}`;
  return headers;
}

export async function callTeamServiceAction(
  body: Record<string, unknown>,
  options?: { requireAuth?: boolean },
): Promise<unknown> {
  const requireAuth = options?.requireAuth !== false;
  const url = await getTeamServiceUrl();
  if (!url) throw new Error('Team service API is not configured.');
  const res = await fetch(url, {
    method: 'POST',
    headers: await requestHeaders(requireAuth),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? 'Team service request failed.');
  }
  return res.json();
}

export async function listTeamServices(orgId: string): Promise<TeamService[]> {
  const client = await getAuthedDataClient();
  if (!client.models?.TeamService?.list) return [];
  const res = await client.models.TeamService.list({ filter: { orgId: { eq: orgId } } });
  return (res.data ?? []).map((row: Record<string, unknown>) => mapService(row));
}

export async function listPublicTeamServices(orgId: string): Promise<TeamService[]> {
  const client = await getCatalogClient();
  if (!client.models?.TeamService?.list) return [];
  const res = await client.models.TeamService.list({
    filter: { orgId: { eq: orgId }, isActive: { eq: true } },
  });
  return (res.data ?? []).map((row: Record<string, unknown>) => mapService(row));
}

export async function createTeamService(
  orgId: string,
  ownerUserId: string,
  draft: TeamServiceDraft,
): Promise<TeamService> {
  try {
    const data = (await callTeamServiceAction({
      action: 'createTeamService',
      orgId,
      ownerUserId,
      ...draft,
    })) as { id: string };
    const client = await getAuthedDataClient();
    const res = await client.models.TeamService.get({ id: data.id });
    if (!res.data) throw new Error('Created service not found');
    return mapService(res.data as Record<string, unknown>);
  } catch {
    const client = await getAuthedDataClient();
    const res = await client.models.TeamService.create({
      orgId,
      ownerUserId,
      memberId: draft.memberId ?? undefined,
      name: draft.name.trim(),
      description: draft.description?.trim(),
      durationMin: draft.durationMin,
      priceCents: draft.priceCents,
      category: draft.category,
      isActive: draft.isActive ?? true,
    });
    if (!res.data) throw new Error('Could not create team service');
    return mapService(res.data as Record<string, unknown>);
  }
}

export async function updateTeamService(
  id: string,
  orgId: string,
  patch: Partial<TeamServiceDraft>,
): Promise<void> {
  try {
    await callTeamServiceAction({ action: 'updateTeamService', id, orgId, ...patch });
  } catch {
    const client = await getAuthedDataClient();
    await client.models.TeamService.update({ id, ...patch });
  }
}

export async function deleteTeamService(id: string, orgId: string): Promise<void> {
  try {
    await callTeamServiceAction({ action: 'deleteTeamService', id, orgId });
  } catch {
    const client = await getAuthedDataClient();
    await client.models.TeamService.delete({ id });
  }
}

export async function bulkAssignTeamService(
  orgId: string,
  payload: TeamServiceDraft & { memberIds: string[] | null },
): Promise<void> {
  await callTeamServiceAction({
    action: 'bulkAssignService',
    orgId,
    ...payload,
  });
}

export async function autoAssignAppointment(appointmentId: string): Promise<string | null> {
  const data = (await callTeamServiceAction({
    action: 'autoAssignAppointment',
    appointmentId,
  })) as { assignedMemberId?: string };
  return data.assignedMemberId ?? null;
}

export async function listBookableOrgMembers(orgId: string): Promise<OrgMember[]> {
  try {
    const data = (await callTeamServiceAction(
      {
        action: 'listBookableOrgMembers',
        orgId,
      },
      { requireAuth: false },
    )) as { members?: Record<string, unknown>[] };
    return (data.members ?? []).map((row) => ({
      id: String(row.id),
      orgId: String(row.orgId),
      ownerUserId: String(row.ownerUserId ?? ''),
      userId: String(row.userId ?? ''),
      role: row.role as OrgMember['role'],
      isBookable: true,
      displayName: row.displayName ? String(row.displayName) : undefined,
      avatarUrl: row.avatarUrl ? String(row.avatarUrl) : undefined,
      serviceAvailability: row.serviceAvailability as string[] | undefined,
    }));
  } catch (e) {
    console.warn('[ShopGND] listBookableOrgMembers failed', e);
    return [];
  }
}

export async function updateMemberServiceAvailability(
  memberId: string,
  serviceAvailability: string[],
): Promise<void> {
  const client = await getAuthedDataClient();
  await client.models.OrgMember.update({ id: memberId, serviceAvailability });
}

export async function inviteOrgMember(input: {
  orgId: string;
  inviteEmail: string;
  role: Exclude<OrgMemberRole, 'owner'>;
  displayName?: string;
  isBookable?: boolean;
}): Promise<{ memberId: string; inviteToken: string }> {
  const data = (await callTeamServiceAction({
    action: 'inviteMember',
    orgId: input.orgId,
    inviteEmail: input.inviteEmail.trim().toLowerCase(),
    role: input.role,
    displayName: input.displayName,
    isBookable: input.isBookable,
  })) as { memberId?: string; inviteToken?: string };
  if (!data.memberId || !data.inviteToken) {
    throw new Error('Invite could not be created.');
  }
  return { memberId: data.memberId, inviteToken: data.inviteToken };
}

/** Group services with same name/category for list view (collapse per-member rows). */
export function groupTeamServices(services: TeamService[]) {
  const map = new Map<string, TeamService[]>();
  for (const s of services) {
    const key = `${s.name}::${s.category ?? ''}`;
    const list = map.get(key) ?? [];
    list.push(s);
    map.set(key, list);
  }
  return [...map.entries()].map(([key, items]) => ({
    key,
    name: items[0].name,
    category: items[0].category,
    durationMin: items[0].durationMin,
    priceCents: items[0].priceCents,
    isActive: items.every((i) => i.isActive),
    items,
    memberIds: items.map((i) => i.memberId).filter(Boolean) as string[],
    allMembers: items.some((i) => !i.memberId),
  }));
}
